/* Shop Grid filtering / sidebar behavior.
 * Renders client-side OR-within-group + AND-across-groups filters,
 * "would-be-added" counts per option, and mobile sidebar toggle.
 * Idempotent: safe to run multiple times (e.g. on Shopify section re-render).
 */
(() => {
  const root = document.querySelector('[data-ve-shop]');
  if (!root || root.dataset.veShopBound === 'true') return;
  root.dataset.veShopBound = 'true';

  const sidebar = root.querySelector('[data-ve-shop-sidebar]');
  const overlay = root.querySelector('[data-ve-shop-overlay]');
  const toggle = root.querySelector('[data-ve-shop-toggle]');
  const clearBtn = root.querySelector('[data-ve-shop-clear]');
  const count = root.querySelector('[data-ve-shop-count]');
  const empty = root.querySelector('[data-ve-shop-empty]');
  const products = Array.from(root.querySelectorAll('[data-ve-shop-product]'));
  const inputs = Array.from(root.querySelectorAll('[data-filter-key]'));

  const SETS = ['type', 'variedad', 'linea', 'estuche', 'volume'];

  // Groups whose values are MULTI-valued in the product metafield
  // (e.g. a Picual+Royal blend has variedad="picual, royal"). For these
  // we apply AND-within-group: selecting two options requires the
  // product to carry BOTH values. For all other groups (single-valued
  // metafields like Tipo, Packs, Volumen) we keep OR-within-group
  // because AND would always produce 0 results when >1 option is checked.
  const AND_SETS = new Set(['variedad', 'linea']);

  // Groups whose product metafield is single-valued (a product is
  // either Aceite OR Ibérico, never both — same for Packs / Volumen).
  // For these groups we enforce MUTUAL EXCLUSION on the checkbox UI:
  // selecting an option auto-deselects every other option in the same
  // group. Without this the user can check both "Aceite" and "Ibérico"
  // (because the markup is a checkbox), the OR filter then resolves
  // to the union (= every product), and the UI looks broken.
  const SINGLE_VALUE_SETS = new Set(['type', 'estuche', 'volume']);

  // Build a filter state. If `excludeKey` is given, that group is
  // skipped — used by countForOption when the target group is OR, so
  // we can compute "what would be added" rather than "what stays".
  const collect = (excludeKey) => {
    const f = { price: { min: null, max: null } };
    SETS.forEach(k => (f[k] = new Set()));
    inputs.forEach(inp => {
      if (inp.dataset.filterKey === 'price') {
        if (excludeKey === 'price') return;
        const v = parseFloat(inp.value);
        if (!isNaN(v)) f.price[inp.dataset.filterBound] = v;
        return;
      }
      if (inp.dataset.filterKey === excludeKey) return;
      if (inp.checked) f[inp.dataset.filterKey].add(inp.value);
    });
    return f;
  };

  const matches = (p, f) => {
    if (f.price.min !== null && parseFloat(p.dataset.price) < f.price.min) return false;
    if (f.price.max !== null && parseFloat(p.dataset.price) > f.price.max) return false;
    for (const k of SETS) {
      if (f[k].size === 0) continue;
      const val = (p.dataset[k] || '').toLowerCase();
      // .trim() each token so a metafield value like "picual, royal"
      // (with a space after the comma) still matches correctly.
      const list = val.split(',').map(s => s.trim()).filter(Boolean);

      if (AND_SETS.has(k)) {
        // AND: product must carry every selected choice.
        for (const choice of f[k]) {
          if (!list.includes(choice)) return false;
        }
      } else {
        // OR: product must carry at least one selected choice.
        let ok = false;
        for (const choice of f[k]) {
          if (k === 'estuche' || k === 'type') {
            if (val === choice) { ok = true; break; }
          } else {
            if (list.includes(choice)) { ok = true; break; }
          }
        }
        if (!ok) return false;
      }
    }
    return true;
  };

  // Per-option "would-be-added" count.
  // - OR groups: count = products that match the other groups AND
  //   carry this option in their own group (the option's value is
  //   added to the union for its group).
  // - AND groups: count = products that match every currently
  //   selected choice in this group PLUS this candidate option,
  //   AND every other group. The new option tightens the filter.
  const countForOption = (key, val) => {
    if (AND_SETS.has(key)) {
      const all = collect();
      all[key].add(val);
      return products.filter(p => matches(p, all)).length;
    }
    const otherFilters = collect(key);
    return products.filter(p => {
      if (!matches(p, otherFilters)) return false;
      const dval = (p.dataset[key] || '').toLowerCase();
      if (key === 'type' || key === 'estuche') return dval === val;
      return dval.split(',').map(s => s.trim()).filter(Boolean).includes(val);
    }).length;
  };

  const toolbarEl = root.querySelector('.ve-shop__toolbar');
  const gridEl = root.querySelector('[data-ve-shop-grid]');
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // After a user-initiated filter change, if the page got short enough
  // that the user is now below the content (looking at footer/whitespace),
  // smoothly scroll back to the toolbar so they see the filtered set.
  const maybeScrollBack = () => {
    if (!gridEl || !toolbarEl) return;
    requestAnimationFrame(() => {
      const rect = gridEl.getBoundingClientRect();
      // grid is entirely above viewport → user is "below the page"
      if (rect.bottom < 80) {
        toolbarEl.scrollIntoView({
          behavior: prefersReduced ? 'auto' : 'smooth',
          block: 'start',
        });
      }
    });
  };

  const apply = (userTriggered) => {
    const f = collect();
    let shown = 0;
    products.forEach(p => {
      const visible = matches(p, f);
      p.dataset.hidden = visible ? 'false' : 'true';
      if (visible) shown++;
    });
    if (count) count.textContent = shown;
    if (empty) empty.hidden = shown !== 0;
    const hasActive =
      inputs.some(i => i.type === 'checkbox' ? i.checked : i.value !== '');
    if (clearBtn) clearBtn.hidden = !hasActive;
    updateCounts();
    if (userTriggered) maybeScrollBack();
  };

  const updateCounts = () => {
    root.querySelectorAll('.ve-shop__opt input[type="checkbox"]').forEach(inp => {
      const key = inp.dataset.filterKey;
      const val = inp.value;
      const label = inp.closest('.ve-shop__opt');
      const countEl = label && label.querySelector('[data-count]');
      if (!countEl) return;
      const n = countForOption(key, val);
      countEl.textContent = n > 0 ? '(' + n + ')' : '';
      // is-empty visually dims options that wouldn't add any
      // products. The checkbox itself stays clickable (see CSS).
      label.classList.toggle('is-empty', n === 0 && !inp.checked);
    });
  };

  inputs.forEach(i => {
    i.addEventListener('change', () => {
      // Mutual exclusion: if the user just checked an option in a
      // single-value group, uncheck every sibling in the same group.
      // Allows re-clicking the same option to deselect (= no filter).
      if (
        i.type === 'checkbox' &&
        i.checked &&
        SINGLE_VALUE_SETS.has(i.dataset.filterKey)
      ) {
        inputs.forEach(other => {
          if (
            other !== i &&
            other.type === 'checkbox' &&
            other.dataset.filterKey === i.dataset.filterKey &&
            other.checked
          ) {
            other.checked = false;
          }
        });
      }
      apply(true);
    });
    if (i.type === 'number') i.addEventListener('input', () => {
      clearTimeout(i._t); i._t = setTimeout(() => apply(true), 200);
    });
  });

  if (clearBtn) clearBtn.addEventListener('click', () => {
    inputs.forEach(i => {
      if (i.type === 'checkbox') i.checked = false;
      else i.value = '';
    });
    apply(true);
  });

  const openSidebar = () => {
    sidebar.dataset.open = 'true';
    overlay.dataset.open = 'true';
    document.body.style.overflow = 'hidden';
  };
  const closeSidebar = () => {
    sidebar.dataset.open = 'false';
    overlay.dataset.open = 'false';
    document.body.style.overflow = '';
  };
  if (toggle) toggle.addEventListener('click', openSidebar);
  if (overlay) overlay.addEventListener('click', closeSidebar);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && sidebar.dataset.open === 'true') closeSidebar();
  });

  apply();
})();
